import { useMutation } from "@tanstack/react-query"
import { Plus, Trash2 } from "lucide-react"
import { type FormEvent, useState } from "react"

import { api } from "@/api/cliente"
import { Boton } from "@/componentes/ui/Boton"
import { Insignia } from "@/componentes/ui/Insignia"
import { comoFecha, FECHA_MINIMA } from "@/lib/fechas"

import { ESTADOS_ETAPA, type EstadoEtapa, NOMBRE_ESTADO, type Objetivo, TONO_ESTADO } from "./tipos"

/**
 * Los hitos de un objetivo.
 *
 * ⚠ **Marcar una etapa como completada es lo que mueve el avance.** No hay ningún
 * sitio donde teclear un porcentaje, y es deliberado: el número sale de contar hitos.
 *
 * ⚠ **El estado se cambia con un selector, no marcando una casilla.** Son tres
 * estados, no dos: «en progreso» existe para poder decir que algo se está haciendo sin
 * que cuente como hecho. Una casilla sólo puede decir sí o no.
 */

export function ListaEtapas({
  objetivo,
  puedeEditar,
  alCambiar,
}: {
  objetivo: Objetivo
  puedeEditar: boolean
  alCambiar: () => void
}) {
  const [anadiendo, setAnadiendo] = useState(false)
  const [titulo, setTitulo] = useState("")
  const [fecha, setFecha] = useState("")

  const crear = useMutation({
    // Sin estado de error en la pantalla: fallaba en silencio (auditoría 12/10).
    meta: { error: "No se pudo añadir la etapa" },
    mutationFn: () =>
      api.post(`/api/v1/estrategia/objetivos/${objetivo.id}/etapas`, {
        titulo,
        fecha: fecha || null,
      }),
    onSuccess: () => {
      alCambiar()
      setTitulo("")
      setFecha("")
      setAnadiendo(false)
    },
  })

  const cambiar = useMutation({
    // Sin estado de error en la pantalla: fallaba en silencio (auditoría 12/10).
    meta: { error: "No se pudo actualizar la etapa" },
    mutationFn: ({ id, estado }: { id: number; estado: EstadoEtapa }) =>
      api.patch(`/api/v1/estrategia/etapas/${id}`, { estado }),
    onSuccess: alCambiar,
  })

  const borrar = useMutation({
    // Sin estado de error en la pantalla: fallaba en silencio (auditoría 12/10).
    meta: { error: "No se pudo borrar la etapa" },
    mutationFn: (id: number) => api.delete(`/api/v1/estrategia/etapas/${id}`),
    onSuccess: alCambiar,
  })

  const estilo = {
    borderColor: "var(--border)",
    backgroundColor: "var(--surface)",
    color: "var(--fg)",
  }

  function enviar(evento: FormEvent) {
    evento.preventDefault()
    if (titulo.trim()) crear.mutate()
  }

  return (
    <div className="mt-3">
      {objetivo.etapas.length === 0 && (
        <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
          Sin hitos: un objetivo sin etapas avanza 0 %.
        </p>
      )}

      <ul className="space-y-1">
        {objetivo.etapas.map((etapa) => (
          <li key={etapa.id} className="flex flex-wrap items-center gap-2 text-sm">
            <Insignia tono={TONO_ESTADO[etapa.estado]}>{NOMBRE_ESTADO[etapa.estado]}</Insignia>
            <span className="min-w-0 flex-1 truncate">{etapa.titulo}</span>
            {etapa.fecha && (
              <span className="text-xs tabular" style={{ color: "var(--fg-muted)" }}>
                {comoFecha(etapa.fecha)}
              </span>
            )}
            {puedeEditar && (
              <>
                <select
                  aria-label={`Estado de ${etapa.titulo}`}
                  value={etapa.estado}
                  onChange={(e) =>
                    cambiar.mutate({ id: etapa.id, estado: e.target.value as EstadoEtapa })
                  }
                  className="h-7 rounded-[var(--radius-control)] border px-1 text-xs"
                  style={estilo}
                >
                  {ESTADOS_ETAPA.map((e) => (
                    <option key={e} value={e}>
                      {NOMBRE_ESTADO[e]}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => borrar.mutate(etapa.id)}
                  aria-label={`Eliminar ${etapa.titulo}`}
                  style={{ color: "var(--destructive)" }}
                >
                  <Trash2 size={13} aria-hidden="true" />
                </button>
              </>
            )}
          </li>
        ))}
      </ul>

      {puedeEditar &&
        (anadiendo ? (
          <form onSubmit={enviar} className="mt-2 flex flex-wrap items-end gap-2">
            <div className="min-w-[180px] flex-1">
              <label
                htmlFor={`etapa-${objetivo.id}`}
                className="mb-1 block text-[11px] font-medium uppercase"
              >
                Hito
              </label>
              <input
                id={`etapa-${objetivo.id}`}
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                maxLength={300}
                className="h-8 w-full rounded-[var(--radius-control)] border px-2 text-sm"
                style={estilo}
              />
            </div>
            <div>
              <label
                htmlFor={`fecha-${objetivo.id}`}
                className="mb-1 block text-[11px] font-medium uppercase"
              >
                Fecha
              </label>
              <input
                id={`fecha-${objetivo.id}`}
                type="date"
                min={FECHA_MINIMA}
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
                className="h-8 rounded-[var(--radius-control)] border px-2 text-sm tabular"
                style={estilo}
              />
            </div>
            <Boton variante="primario" tamano="sm" type="submit" disabled={crear.isPending}>
              Añadir
            </Boton>
            <Boton variante="fantasma" tamano="sm" onClick={() => setAnadiendo(false)}>
              Cancelar
            </Boton>
          </form>
        ) : (
          <Boton
            variante="fantasma"
            tamano="sm"
            className="mt-2"
            onClick={() => setAnadiendo(true)}
          >
            <Plus size={13} aria-hidden="true" />
            Hito
          </Boton>
        ))}
    </div>
  )
}
